/**
 * Der Meldungsspeicher des Fehlerstapels (Fehleranzeige Paket 1, Spec
 * `docs/superpowers/specs/2026-10-19-fehleranzeige-fundament-design.md`, Abschnitt 1) - der zweite
 * Meldungskanal neben dem Toast (`components/App/Toast.tsx`). Der Toast bleibt fuer Bestaetigungen
 * und neutrale Hinweise (2,2 s, ein Slot); alles, was sagt, dass die Absicht des Nutzers nicht oder
 * nur teilweise ausgefuehrt wurde, geht hierher und BLEIBT STEHEN, bis es weggeklickt wird.
 *
 * Ein Modul mit Zustand auf Modulebene, kein React-Context: `meldeFehler` muss aus `api/*`,
 * `state/*`, dem globalen Auffaenger und aus Haken erreichbar sein, die in `App.tsx` selbst laufen,
 * also OBERHALB jedes Providers, den `App.tsx` rendern koennte. Die Anzeige (`FehlerStapel.tsx`)
 * liest den Zustand ueber `useSyncExternalStore`.
 *
 * Vertrag, auf den sich Aufrufer und Anzeige verlassen:
 * - `aktuelleFehler()` liefert OHNE Aenderung dieselbe Referenz. `useSyncExternalStore` ruft den
 *   Schnappschuss bei jedem Render; eine frische Liste je Aufruf liesse React endlos neu rendern.
 *   Deshalb wird nie veraendert, sondern bei jeder echten Aenderung eine neue Liste gesetzt, und ein
 *   Aufruf ohne Wirkung (unbekannte id, leerer Stapel) setzt gar nichts und benachrichtigt niemanden.
 * - Die Liste ist neueste-zuerst. Ein Text, der schon offen ist, bekommt einen hoeheren Zaehler und
 *   RUECKT NACH OBEN: sonst bliebe die Wiederholung hinter "+ N weitere" unsichtbar, obwohl der Fehler
 *   gerade eben wieder aufgetreten ist. Die id bleibt, der Eintrag wird verschoben, nicht neu angelegt.
 * - Zusammengefasst wird nur gegen OFFENE Eintraege. Nach dem Schliessen beginnt dieselbe Meldung
 *   wieder bei 1: der Nutzer hat die vorige gesehen und weggeklickt, die neue ist neue Information.
 */
import { useSyncExternalStore } from "react";
import { nachladeFehlertext } from "../lib/ausgangUnklar.ts";
import { fehlertext } from "../lib/fehlertext.ts";

export interface FehlerEintrag {
  readonly id: number;
  readonly text: string;
  /** Wie oft derselbe Text gemeldet wurde, solange dieser Eintrag offen ist (mindestens 1). */
  readonly anzahl: number;
}

/** Hoechstens so viele Eintraege zeigt die Anzeige auf einmal; aeltere fasst sie zusammen. */
export const FEHLER_SICHTBAR_MAX = 5;

const LEER: readonly FehlerEintrag[] = [];

let eintraege: readonly FehlerEintrag[] = LEER;
let naechsteId = 1;
const abonnenten = new Set<() => void>();

function setze(neu: readonly FehlerEintrag[]): void {
  eintraege = neu;
  // Kopie, weil ein Abonnent beim Benachrichtigen abbestellen darf (Unmount).
  for (const abonnent of [...abonnenten]) abonnent();
}

function abonnieren(abonnent: () => void): () => void {
  abonnenten.add(abonnent);
  return () => {
    abonnenten.delete(abonnent);
  };
}

/**
 * Meldet einen Fehler. `ursache` ist ein Fehlerobjekt (der Text kommt dann aus `fehlertext`) ODER ein
 * fertiger deutscher Satz; `kontext` sagt, was scheiterte ("Die Position konnte nicht gespeichert
 * werden."). Ergibt `fehlertext` `null` (Abbruch, 401), passiert nichts: den Abbruch hat der Nutzer
 * selbst ausgeloest, den 401 uebernimmt die Anmeldemaske.
 */
export function meldeFehler(ursache: unknown, kontext?: string): void {
  eintragen(fehlertext(ursache, kontext));
}

/**
 * Meldet ein gescheitertes Nachladen, neben dem der Stapel keinen Wiederholungsrat zeigen darf (R4: "Das Dokument ist gespeichert,
 * die Anzeige konnte aber nicht aktualisiert werden."). Gleiche Argumente wie `meldeFehler(ursache, kontext)`, eine
 * Stelle wird also umgestellt, indem man den Namen tauscht. Der Unterschied: Raet die Ursache zum Wiederholen
 * (`URSACHEN_MIT_WIEDERHOLUNGSRAT`: Netz, Zeitlimit, 5xx ...), steht NUR `satz` im Stapel. Hinter einem Erfolgssatz
 * liesse "... und erneut versuchen" den Nutzer die Aktion ein zweites Mal ausloesen. Eine Ursache ohne Rat (403,
 * Servertext) wird angehaengt. Bei `null` (Abbruch, 401) passiert nichts.
 *
 * Zwei Faelle gehoeren hierher (Fehleranzeige Paket 2, `docs/superpowers/specs/2026-10-23-fehleranzeige-ladefehler-design.md`):
 * - **R4:** eine Aktion ist gelungen, nur das Nachladen danach scheiterte; `satz` sagt, was gespeichert ist und dass die
 *   Anzeige nicht aktuell ist.
 * - **L4:** die Anwendung laedt von selbst nach (Live-Verbindung, Hintergrundlauf), niemand hat etwas ausgeloest; `satz`
 *   sagt, dass die Anzeige nicht aktuell ist und was zu tun ist ("Bitte neu laden."); ein zweiter Rat der Ursache
 *   daneben wuerde ihm nur widersprechen oder ihn doppeln.
 *
 * NICHT fuer ein Laden, das der Mensch ausgeloest hat und das nichts geschrieben hat (Seite oeffnen, "Erneut laden",
 * Neuladen mit Altstand, L3): dort stimmt der Rat der Ursache, und es bleibt bei `meldeFehler`.
 */
export function meldeNachladeFehler(ursache: unknown, satz: string): void {
  eintragen(nachladeFehlertext(ursache, satz));
}

/**
 * R4 OHNE Ursache (Fehleranzeige Paket 2, Ruling R-T11a): die Aktion ist gelungen, das Nachladen danach lieferte `null` -
 * die Bestellung gibt es nicht mehr -, und kein Fehler erklaert, warum die Anzeige nicht aktuell ist. Dann steht der
 * Satz allein (Spec "Nachladen nach einem Schreiben", Punkt 2). Er laeuft wie jeder Eintrag mit demselben Text
 * zusammen, auch mit dem von `meldeNachladeFehler`.
 *
 * Warum nicht `meldeFehler(satz)`, das dasselbe tut: der Waechter (`test/helpers/fehlerWaechter.ts`, W2) verlangt fuer
 * einen R4-Satz `meldeNachladeFehler` bzw. diesen Namen, damit nirgends ein "Gespeichert, die Anzeige ist aber nicht
 * mehr aktuell" mit dem Wiederholungsrat einer Ursache hinter `meldeFehler` landet. Der Name sagt, welcher Fall
 * gemeint ist. Gibt es eine Ursache, ist `meldeNachladeFehler` richtig.
 */
export function meldeNachladeSatz(satz: string): void {
  eintragen(fehlertext(satz));
}

function eintragen(text: string | null): void {
  if (text === null) return;

  // Eine Wiederholung rueckt nach oben: sonst bliebe sie hinter "+ N weitere" unsichtbar, obwohl der
  // Fehler gerade eben wieder aufgetreten ist. Die id bleibt, der Eintrag wird verschoben, nicht neu.
  const vorhanden = eintraege.find((eintrag) => eintrag.text === text);
  if (vorhanden !== undefined) {
    setze([
      { ...vorhanden, anzahl: vorhanden.anzahl + 1 },
      ...eintraege.filter((eintrag) => eintrag !== vorhanden),
    ]);
    return;
  }
  setze([{ id: naechsteId++, text, anzahl: 1 }, ...eintraege]);
}

export function fehlerSchliessen(id: number): void {
  if (!eintraege.some((eintrag) => eintrag.id === id)) return;
  const rest = eintraege.filter((eintrag) => eintrag.id !== id);
  setze(rest.length === 0 ? LEER : rest);
}

export function alleFehlerSchliessen(): void {
  if (eintraege.length === 0) return;
  setze(LEER);
}

/** Neueste zuerst. Dieselbe Referenz, solange sich nichts aendert. */
export function aktuelleFehler(): readonly FehlerEintrag[] {
  return eintraege;
}

export function useFehlerStapel(): readonly FehlerEintrag[] {
  return useSyncExternalStore(abonnieren, aktuelleFehler, aktuelleFehler);
}

/**
 * NUR FUER TESTS (`test/setup.ts`, `afterEach` NACH `cleanup()`): leert den Stapel und setzt den
 * Zaehler zurueck. Ohne diesen Aufruf sieht jeder Testfall die Eintraege des vorigen, weil der
 * Speicher auf Modulebene lebt und eine Testdatei sich ein Modul teilt.
 */
export function fehlerStapelZuruecksetzen(): void {
  naechsteId = 1;
  alleFehlerSchliessen();
}
